import { NgTemplateOutlet } from '@angular/common';
import { afterRenderEffect, ChangeDetectionStrategy, Component, computed, ElementRef, inject } from '@angular/core';
import { FANS, RANGE } from '../content';
import { RangeTab } from '../range-tab';
import { RevealDirective } from '../reveal.directive';

/**
 * The range, browsed by category: a row of chip tabs (the categories in
 * littlenap.in's menu) over the products of whichever one is selected — one
 * on a large card, two on small cards beside it, all like the Best Sellers cards. The header's Collections
 * menu can pick the category too.
 */
@Component({
  selector: 'app-collections',
  imports: [NgTemplateOutlet, RevealDirective],
  templateUrl: './collections.html',
  styleUrl: './collections.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Collections {
  /** each category with its cards told apart: the large one, and the small ones beside it */
  protected readonly tabs = RANGE.map((tab) => ({ ...tab, feature: tab.items[0], side: tab.items.slice(1, 3) }));
  protected readonly fans = FANS;
  protected readonly stars = [1, 2, 3, 4, 5];

  private readonly range = inject(RangeTab);
  /** index of the category on show */
  protected readonly active = computed(() => Math.max(0, this.tabs.findIndex((tab) => tab.id === this.range.id())));

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  constructor() {
    // On phones the chips are one scrolling row: bring the chosen one to the middle of it. Only the
    // row is scrolled, never the page — the header's menu is scrolling the page here at the same time.
    afterRenderEffect(() => {
      const tab = this.tabElement(this.active());
      const row = tab?.parentElement;
      if (!tab || !row || row.scrollWidth <= row.clientWidth) return;
      const gap = tab.getBoundingClientRect().left - row.getBoundingClientRect().left;
      row.scrollTo({ left: row.scrollLeft + gap - (row.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
    });
  }

  protected select(index: number): void {
    this.range.id.set(this.tabs[index].id);
  }

  private tabElement(index: number): HTMLElement | null {
    return this.host.querySelector<HTMLElement>(`#range-tab-${this.tabs[index].id}`);
  }

  /** Arrow keys move between tabs, Home and End jump to the ends — the usual tab-list keys. */
  protected onKey(event: KeyboardEvent, index: number): void {
    const last = this.tabs.length - 1;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(next);
    this.tabElement(next)?.focus({ preventScroll: true });
  }
}
